import getCheckoutLoyaltyBurningPointItems from 'checkout/selectors/loyalty/getCheckoutLoyaltyBurningPointItems'
import getCheckoutLoyaltyItemsPriceInCurrency from 'checkout/selectors/loyalty/getCheckoutLoyaltyItemsPriceInCurrency'
import getCheckoutLoyaltyUnearnableCreditAmount from 'checkout/selectors/loyalty/getCheckoutLoyaltyUnearnableCreditAmount'
import getAllItemsDiscountTotals from 'checkout/selectors/payment/getAllItemsDiscountTotals'
import getCheckoutTotalsView from 'checkout/selectors/payment/getCheckoutTotalsView'
import { isUsingCredit } from 'checkout/selectors/view/generic'
import getAllItemViews from 'checkout/selectors/view/getAllItemViews'
import { EmptyMap } from 'lib/array/arrayUtils'
import buildPointsKey from 'loyalty/lib/pointsCalculation/buildPointsKey'
import getCheckoutPointsCalculationRequests from 'checkout/selectors/loyalty/pointsCalculation/getCheckoutPointsCalculationRequests'
import { createSelector } from 'reselect'
import { getLoyaltyBonusPointsCampaignTypes } from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import { getAgentHubCommissionMap } from 'agentHub/selectors/agentHubSelectors'
import { getCalculationRequestAfterCommissionIfAgentHubEnabled } from 'lib/agentHub/agentHubUtils'
import calculateAdjustedEarnTotals from 'loyalty/lib/pointsCalculation/calculateAdjustedEarnTotals'
import { getEarnableLoyaltyPaymentMethodBonusPoints } from 'loyalty/lib/paymentMethodEarn/loyaltyPaymentMethodEarnRules'

const getCheckoutLoyaltyPointsEarnItemTotals = createSelector(
  (state: App.State) => state.loyalty.pointsEarnCalculations,
  (state: App.State) => state.auth.account.memberId,
  (state: App.State) => getCheckoutPointsCalculationRequests(state),
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getCheckoutLoyaltyItemsPriceInCurrency(state),
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => getCheckoutLoyaltyBurningPointItems(state),
  (state: App.State) => getCheckoutTotalsView(state),
  (state: App.State) => getCheckoutLoyaltyUnearnableCreditAmount(state),
  (state: App.State) => isUsingCredit(state),
  (state: App.State) => getAgentHubCommissionMap(state),
  (
    pointsEarnCalculations,
    memberId,
    calculationEarnRequests,
    allItemViews,
    itemsPriceInCurrency,
    allItemsDiscountTotals,
    burningPointItems,
    checkoutTotalsView,
    unearnableCreditAmount,
    isUsingCredit,
    agentHubCommissionMap,
  ): Map<string, App.Checkout.LoyaltyPointsEarnItemTotals> => {
    if (!checkoutTotalsView.hasRequiredData) return EmptyMap

    const totalsPointsEarn = new Map<string, App.Checkout.LoyaltyPointsEarnItemTotals>()

    calculationEarnRequests.forEach(calculationEarnRequest => {
      if (!calculationEarnRequest.itemId) return

      const itemView = allItemViews.data.get(calculationEarnRequest.itemId)?.data

      if (!itemView) return

      const itemId = itemView.item.itemId

      const itemPricing = itemsPriceInCurrency.get(itemId)
      /**
       * This always needs to be the payable price which is then used for adjusting points ratios
       */
      const defaultPayableItemPrice = itemPricing?.price

      const newCalculationEarnRequest = getCalculationRequestAfterCommissionIfAgentHubEnabled(calculationEarnRequest, agentHubCommissionMap)
      const pointsEarnCalculation = pointsEarnCalculations[buildPointsKey(newCalculationEarnRequest, memberId ?? '')]

      if (!pointsEarnCalculation?.data || !defaultPayableItemPrice) return

      const initialBasePoints = pointsEarnCalculation.data.basePoints
      const initialBonusPoints = pointsEarnCalculation.data.bonusPoints
      const initialStatusCredits = pointsEarnCalculation.data.statusCredits
      const initialBonusStatusCredits = pointsEarnCalculation.data.bonusStatusCredits

      // Deduct promo off payable price
      const itemDiscount = allItemsDiscountTotals.get(itemView.item.itemId) ?? 0

      // Deduct burnable points off payable price
      const burningPointItem = burningPointItems.find((burningPointItem) => burningPointItem.itemId === itemId)

      let adjustedItemPrice = defaultPayableItemPrice - itemDiscount - (burningPointItem?.pointsPaidInCurrency ?? 0)

      const {
        grandTotal,
        amountPayableByCredit,
        serviceFee = 0,
        merchantFee = 0,
        otherFees,
        carHirePayOnArrival = 0,
      } = checkoutTotalsView.data

      const propertyFees = otherFees?.propertyFees ?? 0

      const grandTotalPreCreditsApplied = (grandTotal - serviceFee - merchantFee - propertyFees - carHirePayOnArrival) + amountPayableByCredit

      const itemWeightedCreditRatio = adjustedItemPrice / grandTotalPreCreditsApplied
      const itemUnearnableCreditAmount = isUsingCredit ? itemWeightedCreditRatio * unearnableCreditAmount : 0

      // Deduct unearnable credits off payable price
      adjustedItemPrice -= itemUnearnableCreditAmount

      const {
        earnableBasePoints,
        earnableBonusPoints,
        earnableStatusCredits,
        earnableBonusStatusCredits,
        earnablePoints,
      } = calculateAdjustedEarnTotals({
        defaultPayableItemPrice,
        adjustedItemPrice,
        basePoints: initialBasePoints,
        bonusPoints: initialBonusPoints,
        statusCredits: initialStatusCredits,
        bonusStatusCredits: initialBonusStatusCredits,
        roundingEnabled: pointsEarnCalculation.data.roundingEnabled,
        earnCap: itemView.loyalty.earnCap,
      })

      // The payment-method bonus has its own messaging, so campaign labels only count campaign bonuses
      const earnablePaymentMethodBonusPoints = getEarnableLoyaltyPaymentMethodBonusPoints({
        customRulesBreakdown: pointsEarnCalculation.data.customRulesBreakdown,
        initialBonusPoints,
        earnableBonusPoints,
      })

      const bonusPointsCampaignTypes = getLoyaltyBonusPointsCampaignTypes({
        basePoints: earnableBasePoints,
        bonusPoints: earnableBonusPoints - earnablePaymentMethodBonusPoints,
        bonusStatusCredits: earnableBonusStatusCredits,
        statusCredits: earnableStatusCredits,
      })

      totalsPointsEarn.set(itemId, {
        defaultPayableItemPrice,
        initialBasePoints,
        initialStatusCredits,
        initialBonusStatusCredits,
        itemPromoDiscount: itemDiscount,
        pointsPaidInCurrency: burningPointItem?.pointsPaidInCurrency ?? 0,
        itemUnearnableCreditAmount,
        earnableStatusCredits,
        earnableBonusStatusCredits,
        earnCap: itemView.loyalty.earnCap,
        initialBonusPoints,
        /**
         * @todo the following needs proper adjustments
         */
        earnableBasePoints,
        earnableBonusPoints,
        earnablePaymentMethodBonusPoints,
        earnablePoints,
        appliedRuleIds: pointsEarnCalculation.data.appliedRuleIds,
        customRulesBreakdown: pointsEarnCalculation.data.customRulesBreakdown ?? [],
        bonusPointsCampaignTypes,
      })
    })

    if (!totalsPointsEarn.size) return EmptyMap

    return totalsPointsEarn
  },
)

export default getCheckoutLoyaltyPointsEarnItemTotals
